import React, { useMemo, useState } from 'react';
import {
  Flex,
  Alert,
  Modal,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooterButtons,
  Text,
  Box,
  TextInput,
  Icon,
  useDisclosure,
  Button,
  Link,
  ListItem,
  OrderedList,
} from '@televet/kibble-ui';
import { useValidation } from 'shared/hooks';
import { getZendeskLink } from '../../utils';
import {
  integrationSystemDisplayName,
  IntegrationSystemDisplayNameType,
  useBitwerxData,
  useIntegrationsProvider,
} from 'shared/providers/IntegrationsProvider';
import useClinicUser from 'shared/hooks/useClinicUser';
import { useTracking } from 'shared/providers/TrackingProvider';
import { SettingsPaymentsEvents } from 'shared/providers/TrackingProvider/EventNames';

interface IBitwerxSetupProps {
  isFinalStep: boolean;
}

const BitwerxSetup = ({ isFinalStep }: IBitwerxSetupProps): JSX.Element => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { primaryIntegrationName } = useIntegrationsProvider();
  const { needsBitwerxForPaymentWritebacks, bitwerxPending, bitwerxLoading, requestBitwerx } = useBitwerxData();
  const { validateEmail } = useValidation();
  const { clinicUser } = useClinicUser();
  const [email, setEmail] = useState<string>(clinicUser?.email || '');
  const { trackEvent } = useTracking();

  const [invalidEmail, setInvalidEmail] = useState(false);

  const handleEmailChange = (e: { target: { value: React.SetStateAction<string> } }): void => {
    setInvalidEmail(false);
    setEmail(e.target.value);
  };

  const handleSubmit = async (): Promise<void> => {
    if (validateEmail(email).isValid) {
      await requestBitwerx({ clinicContactEmail: email, forPayments: true });
      trackEvent(SettingsPaymentsEvents.BitwerxInstallationSubmitted, { from: email });
      onClose();
    } else {
      setInvalidEmail(true);
    }
  };

  const handleCancel = (): void => trackEvent(SettingsPaymentsEvents.BitwerxInstallationCanceled);

  const handleSetup = (): void => {
    onOpen();
    trackEvent(SettingsPaymentsEvents.Initiated);
  };

  const successAlertChildren = useMemo((): JSX.Element => {
    if (isFinalStep) {
      return (
        <Text size="sm">{`${
          primaryIntegrationName === integrationSystemDisplayName.AVIMARK ? 'Balances' : 'Invoices'
        } from ${primaryIntegrationName || 'your PIMS'} will now appear in Flow.`}</Text>
      );
    } else {
      return (
        <Text size="sm">
          We&apos;ve notified Bitwerx of your request. It usually takes 1-3 business days to get a response. If you have
          any questions, please reach out to <a href="mailto:support@otto.vet">support@otto.vet</a>
        </Text>
      );
    }
  }, [primaryIntegrationName, isFinalStep]);

  return needsBitwerxForPaymentWritebacks && !bitwerxPending ? (
    <>
      <Button size="sm" w="fit-content" onClick={handleSetup}>
        Setup Payment Integration
      </Button>

      <Modal isOpen={isOpen} onClose={onClose} size="lg">
        <ModalHeader>Setup Payment Integration</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Text as="p" mb={3} fontWeight="bold">
            {`In order to enable invoice reading${
              !isFinalStep ? ' and writebacks in Flow' : ''
            }, we first need to sync your payments data from
            your PIMS.`}
          </Text>
          <OrderedList mb={6}>
            <ListItem>Submit an email below</ListItem>
            <ListItem>
              Bitwerx, our integration partners, will reach out to the email you submit below to coordinate the
              necessary installation needed in your server to allow for your payments data to sync.
            </ListItem>
            <ListItem>
              You will receive an email once the Payments data has been fully synced from your PIMS to Flow.
            </ListItem>
          </OrderedList>

          <Box mb={6}>
            <Text fontWeight="bold" pb={2} as="div">
              Preferred Email
            </Text>
            <TextInput
              type="email"
              value={email}
              autoFocus={true}
              isRequired={true}
              onChange={handleEmailChange}
              isInvalid={invalidEmail}
              errorText="Invalid Email"
            />
          </Box>

          <Box backgroundColor="background.primaryAlpha.hover" borderRadius="md" py={2} px={3}>
            <Flex>
              <Box flexShrink={0} mr={2}>
                <Icon name="infoCircle" variant="info" />
              </Box>
              <Box>
                <Text fontWeight="bold" as="p">
                  Syncing does NOT finalize the Payments integration process
                </Text>
                <Text as="p">
                  Once syncing for your Payment data is complete and you receive a confirmation email, please follow the
                  instructions in that email to finalize the integration process.
                </Text>
              </Box>
            </Flex>
          </Box>
        </ModalBody>
        <ModalFooterButtons onCancel={handleCancel} onSubmit={handleSubmit} isLoading={bitwerxLoading} />
      </Modal>
    </>
  ) : (
    <Flex direction="column" gap={5}>
      <Alert
        status="success"
        title={isFinalStep ? 'Configuration Complete' : undefined}
        hideCloseButton
        py={2}
        titleProps={{ children: null, size: 'xs' }}
      >
        {successAlertChildren}
      </Alert>
      {!isFinalStep && (
        <Alert
          status="info"
          descriptionProps={{
            size: 'sm',
            children: `For reporting writebacks, you'll be able to use an associated Employee that already exists in your PIMS. If you would like to add a new Employee, please follow the instructions below while your payments integration is being handled by Bitwerx:`,
          }}
          hideCloseButton
        >
          <Link
            size="sm"
            iconProps={{ name: 'externalLink' }}
            isExternal
            href={getZendeskLink(primaryIntegrationName as IntegrationSystemDisplayNameType, true)}
            onClick={(): void => trackEvent(SettingsPaymentsEvents.EmployeeAddition)}
            my={1}
          >
            {`How to add an Employee in ${primaryIntegrationName}`}
          </Link>
        </Alert>
      )}
    </Flex>
  );
};

export default BitwerxSetup;
